import { useEffect } from 'react';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type AxiosError, type AxiosResponse } from 'axios';
import { z } from 'zod';

import * as invoicesService from '~/services/invoices.service';
import { type HttpClientError } from '~/utils/APIError';
import { newRelic } from '~/utils/newRelic/newRelic';

import { INVOICE_ITEMS_CACHE_KEY } from '../constants/invoicesCacheKey';

const TRANSACTION_SCHEMA = z.object({
  carrier: z.string(),
  tracking_number: z.string(),
});

// https://shippo.atlassian.net/wiki/spaces/DM/pages/103481392/API+INVOICEITEM+Data+Model
const INVOICE_ITEM_SCHEMA = z.object({
  amount: z.number(),
  carrier: z.string().optional(),
  currency: z.string(),
  description: z.string().nullable(),
  invoice_item_type: z.string(),
  object_created: z.string(),
  object_purpose: z.string().nullable(),
  provider_name: z.string().optional(),
  tracking_number: z.string().optional(),
  transaction: TRANSACTION_SCHEMA.optional(),
});

const INVOICE_ITEMS_SCHEMA = z.object({
  count: z.number(),
  next: z.any(),
  previous: z.any(),
  results: INVOICE_ITEM_SCHEMA.array(),
});

export type InvoiceItemsResponse = z.infer<typeof INVOICE_ITEMS_SCHEMA>;
export type InvoiceItemType = z.infer<typeof INVOICE_ITEM_SCHEMA>;

export const useFetchInvoiceItems = (
  invoiceId: string,
): UseQueryResult<AxiosResponse<InvoiceItemsResponse, AxiosError>, HttpClientError> => {
  const query = useQuery<AxiosResponse<InvoiceItemsResponse, AxiosError>, HttpClientError>({
    queryFn: () => invoicesService.getInvoiceItemsOfInvoice(invoiceId),
    queryKey: [invoiceId, INVOICE_ITEMS_CACHE_KEY],
  });

  useEffect(() => {
    if (query.error)
      newRelic.noticeError('Error while fetching invoice items', { error: query.error });
  }, [query.error]);

  return query;
};
